import React from 'react';
import { useSelector } from 'react-redux';
import { Icon, Popup } from 'semantic-ui-react';
import { getVariationStatus } from './variationStatusConfig';
import { getCloneStatus } from '../voiceCloning/cloneStatusConfig';
import { selectCloneBySampleId } from '../../slices/voiceCloningSlice';
// Single-source-of-truth stylesheet for the emotion-pill badges.
// All geometry, fonts, status fills, and the borderless treatment live
// in this CSS file. The "+ N more" trigger chip in
// `VariationBadgeListDropdown` also keys off the same `.aai-variation-badge`
// base + `.aai-variation-badge--more` modifier, so the chip pixel-aligns
// with the bottom-right badge it covers.
import './variationBadges.css';

/**
 * VariationBadgeList — shared component for rendering character variation emotions
 * in a CSS grid layout with optional status color-coding.
 *
 * Consumes `variationStatusConfig` (the single source of truth shared with
 * `VariationReviewRow` and `VariationStatusLegend`) so the color, icon,
 * label, and description stay identical across OpportunityDetails,
 * OpportunityCard, OpportunitySpreadsheet and the Character Opportunity table.
 *
 * Opt-in voice-clone swatch:
 *   Pass `showCloneSwatch` to additionally render a small trailing colored dot
 *   whose palette mirrors `cloneStatusConfig`. This requires the voiceCloning
 *   Redux slice to have been populated (via `fetchPipeline(userId)`). The swatch
 *   is **off by default** — marketplace-facing surfaces (Voice Actor view) don't
 *   benefit from it and subscribing per-badge on a long grid is wasteful.
 *   Book-Creator-facing surfaces that want it opt in explicitly.
 *
 * Accessibility:
 *  - When a status is present the badge is focusable, carries a rich
 *    aria-label (status + description), and opens a Popup on hover AND focus.
 *  - The icon is rendered in addition to color, so the status remains
 *    distinguishable without color.
 *
 * @param {Object}  props
 * @param {Array}   props.variations         Array of { uuid, emotion, name?, sampleStatus?, sampleUuid? }
 * @param {number}  [props.columns=6]        Number of grid columns
 * @param {Object}  [props.style]            Optional container style override
 * @param {boolean} [props.showIcon=true]    Render the status icon before the name
 * @param {boolean} [props.showCloneSwatch]  When true, render the voice-clone swatch (BC-only)
 */

/**
 * `STATUS_PALETTE` previously held the badge fill/text/border colors as a
 * JS object. The badge itself is now styled entirely via the
 * `.aai-variation-badge` CSS classes in `variationBadges.css`, so the JS
 * map exists only to drive the **trailing voice-clone swatch dot** — a
 * small colored circle whose fill comes from the accent (formerly
 * `borderColor`) for the matching status colour. The swatch is a
 * dynamic, inline-styled DOM node (8×8 rgb circle), not a class candidate.
 *
 * Keys mirror the `color` field on `variationStatusConfig` /
 * `cloneStatusConfig`. If you add a new colour to either of those configs,
 * add a matching entry here AND a matching `.aai-variation-badge--<colour>`
 * rule in `variationBadges.css`.
 */
const SWATCH_ACCENT = {
  grey: '#bbb',
  green: '#28a745',
  blue: '#2185d0',
  purple: '#8e44ad',
  red: '#dc3545',
  teal: '#17a2b8',
  violet: '#8e44ad',
  olive: '#a2b362',
  orange: '#f2711c',
  black: '#000',
};

const DEFAULT_SWATCH_ACCENT = '#ddd';

/**
 * Build the badge `className` from `statusConfig.color`. Returns a string
 * suitable for `<span className={…}>`. If the colour key isn't represented
 * in the CSS modifiers, the base `.aai-variation-badge` (neutral grey)
 * still applies.
 */
function badgeClassName(colorKey) {
  const modifier = colorKey ? ` aai-variation-badge--${colorKey}` : '';
  return `aai-variation-badge${modifier}`;
}

/**
 * Render the compact badge JSX. Pure — no hooks — so this can be reused
 * by both the plain and the clone-aware wrappers below.
 */
function renderBadge({ v, showIcon, statusConfig, cloneConfig, swatchColor }) {
  const ariaLabel = statusConfig
    ? `${v.emotion} — ${statusConfig.label}: ${statusConfig.description}${cloneConfig ? `; clone pipeline ${cloneConfig.label}` : ''}`
    : `${v.emotion} — No recording task`;

  // Geometry, fill, no-border treatment all come from the CSS class. The
  // colour modifier is keyed off `statusConfig.color` (Semantic UI colour
  // name — e.g. 'grey', 'green', 'blue', …).
  const className = badgeClassName(statusConfig ? statusConfig.color : null);

  return (
    <span
      tabIndex={statusConfig ? 0 : undefined}
      aria-label={ariaLabel}
      className={className}
    >
      {showIcon && statusConfig && (
        <Icon
          name={statusConfig.icon}
          style={{ margin: 0, lineHeight: 1, fontSize: '0.8rem' }}
        />
      )}
      <span style={{ lineHeight: 1 }}>{v.emotion}</span>
      {swatchColor && (
        <span
          aria-hidden="true"
          title={cloneConfig ? `Voice clone: ${cloneConfig.label}` : undefined}
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            marginLeft: '2px',
            backgroundColor: swatchColor,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
            flexShrink: 0,
          }}
        />
      )}
    </span>
  );
}

/**
 * Wrap the given `badge` in a Popup so the status + (optionally) clone
 * description is discoverable on hover/focus. Pure presentational.
 */
function wrapInPopup({ badge, statusConfig, cloneConfig, keyBase }) {
  return (
    <Popup
      key={keyBase}
      on={['hover', 'focus']}
      position="top center"
      trigger={badge}
      header={
        <span>
          <Icon name={statusConfig.icon} color={statusConfig.color} />
          {statusConfig.label}
          {cloneConfig && (
            <span style={{ marginLeft: 8, fontSize: '0.85em', opacity: 0.85 }}>
              · clone: {cloneConfig.label}
            </span>
          )}
        </span>
      }
      content={
        <>
          <div>{statusConfig.description}</div>
          {cloneConfig && (
            <div style={{ marginTop: 4, fontSize: '0.9em', color: '#555' }}>
              <Icon name={cloneConfig.icon} color={cloneConfig.color} />
              Voice clone pipeline — {cloneConfig.description}
            </div>
          )}
        </>
      }
    />
  );
}

/**
 * Plain badge — no Redux subscription. Used on the marketplace and every
 * other voice-actor-facing surface where the voiceCloning slice is
 * intentionally empty.
 */
const BadgeNoClone = ({ v, showIcon, keyBase }) => {
  // Coerce missing/null `sampleStatus` to 'waiting' so every badge gets a
  // colored swatch + icon + popup — same behavior as `VariationReviewRow`
  // (`status = variation.sampleStatus || 'waiting'`). This eliminates the
  // colorless-fallback footgun where a variation existed but its recording
  // task hadn't been instantiated yet would render as an uninformative
  // neutral pill on the marketplace while showing as a proper grey "Waiting"
  // label on the Manage page. Both surfaces now go through the same single
  // source of truth in `variationStatusConfig.js`.
  const statusConfig = getVariationStatus(v.sampleStatus || 'waiting');

  const badge = renderBadge({
    v,
    showIcon,
    statusConfig,
    cloneConfig: null,
    swatchColor: null,
  });

  return wrapInPopup({ badge, statusConfig, cloneConfig: null, keyBase });
};

/**
 * Clone-aware badge — subscribes to `selectCloneBySampleId(sampleUuid)` so
 * the trailing swatch reflects the current pipeline state. Only rendered
 * when the parent passes `showCloneSwatch`.
 */
const BadgeWithClone = ({ v, showIcon, keyBase }) => {
  // Same coercion as BadgeNoClone — see comment above. The clone-aware
  // wrapper additionally renders a trailing pipeline swatch.
  const statusConfig = getVariationStatus(v.sampleStatus || 'waiting');

  // Only subscribe to voiceCloning when we have a sample identifier that
  // can plausibly match the slice's row keyings.
  const cloneRow = useSelector(selectCloneBySampleId(v.sampleUuid || v.uuid));
  const cloneConfig =
    cloneRow && cloneRow.cloneStatus && cloneRow.cloneStatus !== 'none'
      ? getCloneStatus(cloneRow.cloneStatus)
      : null;
  // Swatch fill = the accent colour for the clone-status's colour key.
  // (Previously this was the badge palette's `borderColor` — same value,
  // semantically clearer now that the badge itself doesn't carry a border.)
  const swatchColor = cloneConfig
    ? SWATCH_ACCENT[cloneConfig.color] || DEFAULT_SWATCH_ACCENT
    : null;

  const badge = renderBadge({
    v,
    showIcon,
    statusConfig,
    cloneConfig,
    swatchColor,
  });

  return wrapInPopup({ badge, statusConfig, cloneConfig, keyBase });
};

const VariationBadgeList = ({
  variations = [],
  columns = 6,
  style = {},
  showIcon = true,
  showCloneSwatch = false,
}) => {
  if (!variations || variations.length === 0) return null;

  const validVariations = variations.filter((v) => v && v.uuid && v.emotion);
  if (validVariations.length === 0) return null;

  const BadgeComponent = showCloneSwatch ? BadgeWithClone : BadgeNoClone;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: '4px',
        alignItems: 'center',
        ...style,
      }}
    >
      {validVariations.map((v, i) => (
        <BadgeComponent
          key={v.uuid || i}
          keyBase={v.uuid || i}
          v={v}
          showIcon={showIcon}
        />
      ))}
    </div>
  );
};

export default VariationBadgeList;
